
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Modal, Form, Alert, Badge } from 'react-bootstrap';
import { FiMonitor, FiClock, FiUser, FiArrowRight, FiLock, FiCheckCircle } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const TabletMode = () => {
    const navigate = useNavigate();
    const [rooms, setRooms] = useState([]);
    const [selectedRoom, setSelectedRoom] = useState(null);
    const [roomTrainings, setRoomTrainings] = useState([]);
    const [selectedTraining, setSelectedTraining] = useState(null);
    
    // Modal controls
    const [showTrainingsModal, setShowTrainingsModal] = useState(false);
    const [showPinModal, setShowPinModal] = useState(false);
    
    // Pin/Matricula input
    const [pin, setPin] = useState('');
    const [pinError, setPinError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchRooms();
    }, []);

    const fetchRooms = async () => {
        try {
            const response = await api.get('/rooms');
            // Filter only active rooms
            setRooms(response.data.filter(r => r.ativa));
        } catch (error) {
            console.error('Erro ao buscar salas', error);
        }
    };

    const handleRoomClick = async (room) => {
        setSelectedRoom(room);
        setLoading(true);
        try {
            const response = await api.get(`/rooms/${room.id}/trainings`);
            setRoomTrainings(response.data);
            setShowTrainingsModal(true);
        } catch (error) {
            console.error('Erro ao buscar treinamentos da sala', error);
        } finally {
            setLoading(false);
        }
    };

    const handleTrainingSelect = (training) => {
        setSelectedTraining(training);
        setShowTrainingsModal(false);
        setPin('');
        setPinError('');
        setShowPinModal(true);
    };

    const handlePinSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setPinError('');

        try {
            const response = await api.post('/rooms/auth-pin', {
                training_id: selectedTraining.id,
                matricula: pin // Usamos o state 'pin' para armazenar a matrícula inputada
            });

            if (response.data.authorized) {
                // Redirect to attendance page using the training token
                navigate(`/attendance/${selectedTraining.qr_code_token}`);
            }
        } catch (error) {
            console.error('Erro ao validar Matrícula', error);
            setPinError('Matrícula Inválida ou erro de conexão.');
        } finally {
            setLoading(false);
        }
    };

    const formatDateTime = (isoString) => {
        const date = new Date(isoString);
        return date.toLocaleString('pt-BR', { 
            day: '2-digit', month: '2-digit', year: 'numeric', 
            hour: '2-digit', minute: '2-digit' 
        });
    };

    return (
        <Container className="mt-4 mb-5">
            <div className="text-center mb-5">
                <h1 className="display-4 text-primary fw-bold">Modo Tablet</h1>
                <p className="lead text-muted">Selecione a sala para iniciar o check-in</p>
            </div>

            <Row className="g-4 justify-content-center">
                {rooms.map(room => (
                    <Col key={room.id} xs={12} sm={6} md={4} lg={3}>
                        <Card 
                            className="h-100 shadow-sm border-0 cursor-pointer room-card" 
                            onClick={() => handleRoomClick(room)}
                            style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
                            onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                            onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        >
                            <Card.Body className="d-flex flex-column align-items-center justify-content-center py-5">
                                <FiMonitor size={48} className="text-primary mb-3" />
                                <Card.Title className="h3 text-center mb-2">{room.nome}</Card.Title>
                                <Card.Text className="text-muted text-center small">
                                    {room.descricao || 'Sem descrição'}
                                </Card.Text>
                                <Badge bg="info" className="mt-2">
                                    Capacidade: {room.capacidade || '-'}
                                </Badge>
                            </Card.Body>
                        </Card>
                    </Col>
                ))}
            </Row>

            {/* Modal: Lista de Treinamentos */}
            <Modal show={showTrainingsModal} onHide={() => setShowTrainingsModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Treinamentos na {selectedRoom?.nome}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {roomTrainings.length === 0 ? (
                        <Alert variant="info">
                            Nenhum treinamento pendente encontrado para esta sala nas próximas 12 horas.
                        </Alert>
                    ) : (
                        <div className="list-group">
                            {roomTrainings.map(training => (
                                <button 
                                    key={training.id}
                                    className="list-group-item list-group-item-action d-flex justify-content-between align-items-center p-3"
                                    onClick={() => handleTrainingSelect(training)}
                                >
                                    <div>
                                        <h5 className="mb-1">{training.titulo}</h5>
                                        <p className="mb-1 text-muted">
                                            <FiClock className="me-2" />
                                            {formatDateTime(training.data)}
                                        </p>
                                        <small className="text-muted">
                                            <FiUser className="me-1" />
                                            Gestor: {training.gestor_nome}
                                        </small>
                                    </div>
                                    <FiArrowRight size={24} className="text-primary" />
                                </button>
                            ))}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowTrainingsModal(false)}>Fechar</Button>
                </Modal.Footer>
            </Modal>

            {/* Modal: PIN/Matricula Auth */}
            <Modal show={showPinModal} onHide={() => setShowPinModal(false)} centered backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>Autenticação do Gestor</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handlePinSubmit}>
                    <Modal.Body>
                        <p>Para iniciar o check-in do treinamento <strong>{selectedTraining?.titulo}</strong>, digite a MATRÍCULA do gestor responsável.</p>
                        
                        {pinError && <Alert variant="danger">{pinError}</Alert>}
                        
                        <Form.Group className="mb-3 text-center">
                            <Form.Label><FiLock className="me-2"/>Matrícula do Gestor</Form.Label>
                            <Form.Control 
                                type="text" 
                                placeholder="Digite a Matrícula" 
                                value={pin}
                                onChange={(e) => setPin(e.target.value)}
                                style={{ fontSize: '1.5rem', textAlign: 'center', letterSpacing: '0.1rem' }}
                                autoFocus
                                required
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowPinModal(false)}>Cancelar</Button>
                        <Button variant="primary" type="submit" disabled={loading || !pin}>
                            {loading ? 'Validando...' : 'Iniciar Check-in'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </Container>
    );
};

export default TabletMode;
